import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, type ReactNode } from 'react'

import type { FiscalServiceItem } from '@aeris/contracts'
import {
  Badge,
  Button,
  CheckboxField,
  Combobox,
  Dialog,
  DialogContent,
  DialogFooter,
  Field,
  InlineAlert,
  Input,
  Section,
  Textarea,
} from '@aeris/ui'

import {
  ApiError,
  createFiscalServiceItem,
  fetchFiscalCodes,
  fetchFiscalServiceItems,
  updateFiscalServiceItem,
} from '../lib/api.ts'

/**
 * Os serviços que a empresa presta (F372): o nome que ela usa, com o código da
 * lista nacional e a NBS. Os códigos vêm da lista oficial que está na base. Quem
 * diz qual serve é o contador: esta tela só ajuda a achar.
 */

const QUERY_KEY = ['fiscal-service-items']

const CodePicker = ({
  table,
  label,
  hint,
  value,
  selectedLabel,
  onChange,
}: {
  table: 'service-codes' | 'nbs-codes'
  label: string
  hint: string
  value: string | null
  selectedLabel: string | null
  onChange: (code: string | null, description: string | null) => void
}): ReactNode => {
  const [search, setSearch] = useState('')
  const codes = useQuery({
    queryKey: ['fiscal-codes', table, search],
    queryFn: ({ signal }) => fetchFiscalCodes(table, search, signal),
  })
  const list = codes.data?.codes ?? []
  return (
    <Field label={label} hint={hint}>
      {(field) => (
        <Combobox
          {...field}
          value={value}
          selectedLabel={selectedLabel}
          search={search}
          onSearchChange={setSearch}
          loading={codes.isPending}
          placeholder="Busque por palavra ou número"
          items={list.map((item) => ({
            value: item.code,
            label: item.description,
            hint: item.code,
          }))}
          onValueChange={(next) => {
            const found = list.find((item) => item.code === next)
            onChange(next, found?.description ?? null)
          }}
        />
      )}
    </Field>
  )
}

const ItemDialog = ({
  item,
  onClose,
}: {
  item: FiscalServiceItem | null
  onClose: () => void
}): ReactNode => {
  const queryClient = useQueryClient()
  const [name, setName] = useState(item?.name ?? '')
  const [description, setDescription] = useState(item?.defaultDescription ?? '')
  const [serviceCode, setServiceCode] = useState(item?.serviceCode ?? null)
  const [serviceLabel, setServiceLabel] = useState(
    item?.serviceDescription ?? null
  )
  const [nbsCode, setNbsCode] = useState(item?.nbsCode ?? null)
  const [nbsLabel, setNbsLabel] = useState(item?.nbsCode ?? null)
  const [municipalCode, setMunicipalCode] = useState(item?.municipalCode ?? '')
  const [retained, setRetained] = useState(item?.issRetained ?? false)
  const [rate, setRate] = useState(item?.issRate ?? '')

  const save = useMutation({
    meta: { errorShownInline: true },
    mutationFn: async () => {
      const fields = {
        name: name.trim(),
        defaultDescription: description.trim(),
        serviceCode: serviceCode ?? '',
        nbsCode,
        municipalCode:
          municipalCode.trim() === '' ? null : municipalCode.trim(),
        issRetained: retained,
        issRate:
          !retained || rate.trim() === ''
            ? null
            : rate.trim().replace(',', '.'),
      }
      return item === null
        ? await createFiscalServiceItem(fields)
        : await updateFiscalServiceItem(item.id, {
            ...fields,
            archived: item.archived,
            version: item.version,
          })
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: QUERY_KEY })
      onClose()
    },
  })

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose()
      }}
    >
      <DialogContent
        title={item === null ? 'Novo serviço' : 'Alterar serviço'}
        description="Peça ao contador o código certo para cada serviço."
      >
        <form
          className="flex flex-col gap-3"
          onSubmit={(event) => {
            event.preventDefault()
            save.mutate()
          }}
        >
          <Field label="Nome do serviço">
            {(field) => (
              <Input
                {...field}
                value={name}
                onChange={(event) => {
                  setName(event.target.value)
                }}
              />
            )}
          </Field>
          <Field
            label="Texto que sai na nota"
            hint="Dá para ajustar em cada nota."
          >
            {(field) => (
              <Textarea
                {...field}
                rows={3}
                value={description}
                onChange={(event) => {
                  setDescription(event.target.value)
                }}
              />
            )}
          </Field>
          <CodePicker
            table="service-codes"
            label="Código da lista nacional"
            hint="Obrigatório."
            value={serviceCode}
            selectedLabel={serviceLabel}
            onChange={(code, text) => {
              setServiceCode(code)
              setServiceLabel(text)
            }}
          />
          <CodePicker
            table="nbs-codes"
            label="NBS"
            hint="Vale pedir ao contador junto com o código."
            value={nbsCode}
            selectedLabel={nbsLabel}
            onChange={(code, text) => {
              setNbsCode(code)
              setNbsLabel(text)
            }}
          />
          <Field label="Código municipal" hint="Só se o contador pedir.">
            {(field) => (
              <Input
                {...field}
                value={municipalCode}
                onChange={(event) => {
                  setMunicipalCode(event.target.value)
                }}
              />
            )}
          </Field>
          <CheckboxField
            label="O cliente retém o ISS"
            checked={retained}
            onCheckedChange={(checked) => {
              setRetained(checked === true)
            }}
          />
          {retained ? (
            <Field label="Alíquota do ISS (%)">
              {(field) => (
                <Input
                  {...field}
                  inputMode="decimal"
                  value={rate}
                  onChange={(event) => {
                    setRate(event.target.value)
                  }}
                />
              )}
            </Field>
          ) : null}
          {save.isError ? (
            <InlineAlert tone="danger">
              {save.error instanceof ApiError
                ? save.error.message
                : 'Não foi possível salvar agora.'}
            </InlineAlert>
          ) : null}
          <DialogFooter>
            <Button variant="ghost" onClick={onClose}>
              Voltar
            </Button>
            <Button
              type="submit"
              variant="primary"
              loading={save.isPending}
              disabled={
                name.trim() === '' ||
                description.trim() === '' ||
                serviceCode === null
              }
            >
              Salvar
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

export const FiscalServiceItems = (): ReactNode => {
  const items = useQuery({
    queryKey: QUERY_KEY,
    queryFn: ({ signal }) => fetchFiscalServiceItems(signal),
  })
  const [editing, setEditing] = useState<FiscalServiceItem | 'new' | null>(null)
  const list = items.data?.items ?? []

  return (
    <Section
      title="Serviços"
      description="O que a empresa presta, com o código que sai na nota."
    >
      <div className="flex flex-col gap-3">
        {list.length === 0 ? (
          <p className="text-(length:--text-sm) text-(--text-secondary)">
            Nenhum serviço cadastrado ainda.
          </p>
        ) : (
          <ul className="flex flex-col gap-2">
            {list.map((item) => (
              <li key={item.id} className="flex flex-wrap items-center gap-3">
                <span className="font-medium">{item.name}</span>
                <span className="text-(length:--text-sm) text-(--text-secondary)">
                  {item.serviceCode}
                </span>
                {item.archived ? <Badge tone="neutral">Arquivado</Badge> : null}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setEditing(item)
                  }}
                >
                  Alterar
                </Button>
              </li>
            ))}
          </ul>
        )}
        <div>
          <Button
            onClick={() => {
              setEditing('new')
            }}
          >
            Novo serviço
          </Button>
        </div>
      </div>
      {editing === null ? null : (
        <ItemDialog
          item={editing === 'new' ? null : editing}
          onClose={() => {
            setEditing(null)
          }}
        />
      )}
    </Section>
  )
}
